import React from 'react';
import type { Direction } from '../../hooks/useKonamiCode';

interface LogoWithKonamiProps {
  onDirectionClick?: (dir: Direction) => void;
  progressLength?: number;
}

export function LogoWithKonami({ onDirectionClick, progressLength = 0 }: LogoWithKonamiProps) {
  const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!onDirectionClick) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const width = rect.width;
    const height = rect.height;

    const normX = x / width - 0.5;
    const normY = y / height - 0.5;

    // Detect quadrant based on diagonal angles
    if (Math.abs(normX) > Math.abs(normY)) {
      onDirectionClick(normX > 0 ? 'right' : 'left');
    } else {
      onDirectionClick(normY > 0 ? 'down' : 'up');
    }
  };

  return (
    <div className="relative group flex items-center gap-3">
      {/* Clickable 4-quadrant Logo */}
      <div
        onClick={handleClick}
        title="Namm (Code secret: ↑ ↑ ↓ ↓ ← →)"
        className={`w-10 h-10 border relative overflow-hidden flex items-center justify-center cursor-pointer select-none transition-all duration-300 ${
          progressLength > 0
            ? 'border-namm-cyan shadow-[0_0_15px_rgba(0,229,255,0.4)] scale-105'
            : 'border-namm-line/50 hover:border-namm-cyan/70 bg-namm-panel/70'
        }`}
      >
        {/* Subtle corner ticks */}
        <div className="absolute top-0 left-0 w-1.5 h-1.5 border-t border-l border-namm-cyan" />
        <div className="absolute bottom-0 right-0 w-1.5 h-1.5 border-b border-r border-namm-cyan" />

        {/* Central 'N' */}
        <span className="font-heading font-bold text-sm text-namm-cyan z-10 pointer-events-none">
          N
        </span>

        {/* Directional click overlay indicators on hover */}
        <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none text-[8px] text-namm-muted/60 font-mono">
          <span className="absolute top-0.5 inset-x-0 text-center">▲</span>
          <span className="absolute bottom-0.5 inset-x-0 text-center">▼</span>
          <span className="absolute left-0.5 inset-y-0 flex items-center">◀</span>
          <span className="absolute right-0.5 inset-y-0 flex items-center">▶</span>
        </div>
      </div>

      <div className="flex flex-col">
        <span className="font-heading font-bold text-lg uppercase tracking-wider text-namm-text leading-none">
          namm
        </span>
        {/* Progress dots if combination is being typed */}
        {progressLength > 0 && (
          <div className="flex gap-1 mt-1">
            {[0, 1, 2, 3, 4, 5].map((idx) => (
              <div
                key={idx}
                className={`w-1 h-1 rounded-full transition-colors ${
                  idx < progressLength ? 'bg-namm-cyan' : 'bg-namm-line/40'
                }`}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

